import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchAcquisitionBudget, selectAcquisitionBudget } from '@/store/slices/acquisitionBudget';

import type { BudgetOverride } from '../../mock-data/acquisition';
import { deriveBudgetOverride } from './budget-override';

/**
 * Fetch a brand's acquisition budget (marketing wallet + daily cap) and expose it
 * as a {@link BudgetOverride} for the acquisition score. `ready` stays false only
 * until the GET resolves (see {@link deriveBudgetOverride}), so callers can keep
 * skeletoning the gauge / budget card rather than flashing the mock budget first.
 *
 * Shared by the Acquisition page (gauge + budget card) and the Home dashboard's
 * "Acquisition" optimization-score gauge so both feed the score the SAME real
 * numbers and never disagree. `daily_acquisition_budget` is left undefined when the
 * API has no value, so the builder falls back to the mock profile's daily cap.
 */
export const useAcquisitionBudgetOverride = (
  brandId: string | undefined,
): { override: BudgetOverride | undefined; ready: boolean } => {
  const dispatch = useAppDispatch();
  const acqBudget = useAppSelector((state) => selectAcquisitionBudget(state, brandId));

  React.useEffect(() => {
    if (brandId) dispatch(fetchAcquisitionBudget({ casinoInstanceId: brandId }));
  }, [dispatch, brandId]);

  return React.useMemo(() => deriveBudgetOverride(acqBudget), [acqBudget]);
};
